/**
 * useCreateSubscription - Hook orquestrador para criação de Assinatura (Recorrência)
 *
 * Fluxo em 2 etapas espelhando useCreateCharge:
 *   1) RPC atômica create_financeiro_assinatura cria a regra mãe + N ciclos
 *      (cobranças filhas). Ciclo 1 fica 'pendente'; demais 'aguardando_emissao'.
 *   2) Edge Function financial-charge emite SOMENTE o ciclo 1 no provider.
 *      Ciclos futuros são emitidos pelo job emit-financeiro-ciclos
 *      (cron diário) ou via botão "Antecipar emissão".
 *
 * Falha do provider NÃO reverte os dados locais (ciclo fica 'pendente' e o
 * worker faz o retry/reenvio).
 *
 * @version 1.0.0
 * RELEASE: 2026-06-09 v1.0.0 - Criação inicial (Fase 4 do módulo Recorrência)
 */
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { dispatchCobrancaToProvider } from '@/lib/financial/dispatchCobranca';
import { persistDescricaoTemplate, hasDescricaoVariables } from '@/hooks/financial/useFinanceiroDescricaoTemplate';
import type { ChargeItemInput } from '@/hooks/useCreateCharge';

const logger = createLogger('CreateSubscription');

export type FrequenciaAssinatura =
  | 'semanal'
  | 'quinzenal'
  | 'mensal'
  | 'bimestral'
  | 'trimestral'
  | 'semestral'
  | 'anual'
  | 'bienal';

export type TipoPrazoAssinatura = 'determinado' | 'indeterminado';

/** Payload completo para criação de assinatura */
export interface CreateSubscriptionInput {
  customer_id: string;
  deal_id?: string | null;
  meio_pagamento_id: string;
  conta_corrente_id: string;
  itens: ChargeItemInput[];
  primeiro_vencimento: string; // YYYY-MM-DD
  frequencia: FrequenciaAssinatura;
  tipo_prazo: TipoPrazoAssinatura;
  ciclos_total?: number | null;       // obrigatório quando tipo_prazo='determinado'
  dias_antes_emissao?: number;        // default 12
  // v1.2.0: modo de emissão da cobrança
  tipo_emissao?: 'dias_antes' | 'dia_fixo';
  dia_emissao?: number | null;        // obrigatório quando tipo_emissao='dia_fixo'
  descricao?: string;
  observacoes?: string;
  moeda?: string;
  desconto?: Record<string, unknown>;
  multa?: Record<string, unknown>;
  juros?: Record<string, unknown>;
  reajuste?: Record<string, unknown>;
  tags?: string[];
  metadata?: Record<string, unknown>;
  /** v1.2.0: dias para expirar a cobrança após o vencimento (1..60, default 60). */
  dias_expiracao?: number;
  /** Fase 5: id do cartão tokenizado a ser usado como padrão. */
  default_card_token_id?: string | null;
  /**
   * v1.3.0 — Veículos do cliente vinculados à assinatura (opcional).
   * Quando informado, usa a RPC wrapper atômica
   * `create_financeiro_assinatura_with_vehicles`. Os ciclos gerados herdam os
   * veículos da assinatura em tempo de leitura.
   */
  vehicle_ids?: string[];
}

export function useCreateSubscription() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Guard anti dupla-submissão
  const submittingRef = useRef(false);

  const createSubscription = useMutation({
    mutationFn: async (input: CreateSubscriptionInput) => {
      if (submittingRef.current) {
        throw new Error('Assinatura já está sendo criada. Aguarde.');
      }
      submittingRef.current = true;

      try {
        if (!tenantId) throw new Error('Tenant não encontrado');

        // Valor total = soma dos itens
        const valorTotal = input.itens.reduce(
          (sum, i) => sum + (i.quantidade * i.valor_unitario - i.desconto),
          0,
        );
        const descricaoGeral =
          input.descricao || input.itens.map((i) => i.descricao).join(', ');

        logger.info('Criando assinatura via RPC atômica', {
          frequencia: input.frequencia,
          tipo_prazo: input.tipo_prazo,
          ciclos_total: input.ciclos_total,
          valorTotal,
        });

        // ETAPA 1 — RPC atômica
        // v1.3.0 — wrapper atômico quando há veículos selecionados
        const hasVehicles = (input.vehicle_ids?.length ?? 0) > 0;

        const { data: rpcResult, error: rpcError } = await (supabase.rpc as any)(
          hasVehicles
            ? 'create_financeiro_assinatura_with_vehicles'
            : 'create_financeiro_assinatura',
          {
            p_customer_id: input.customer_id,
            p_meio_pagamento_id: input.meio_pagamento_id,
            p_conta_corrente_id: input.conta_corrente_id,
            p_valor_total: valorTotal,
            p_primeiro_vencimento: input.primeiro_vencimento,
            p_frequencia: input.frequencia,
            p_tipo_prazo: input.tipo_prazo,
            p_ciclos_total: input.ciclos_total ?? null,
            p_dias_antes_emissao: input.dias_antes_emissao ?? 12,
            p_tipo_emissao: input.tipo_emissao ?? 'dias_antes',
            p_dia_emissao: input.tipo_emissao === 'dia_fixo' ? input.dia_emissao ?? null : null,
            p_descricao: descricaoGeral,
            p_observacoes: input.observacoes || null,
            p_deal_id: input.deal_id || null,
            p_moeda: input.moeda || 'BRL',
            p_itens: input.itens,
            p_desconto: input.desconto || {},
            p_multa: input.multa || {},
            p_juros: input.juros || {},
            p_reajuste: input.reajuste || {},
            p_tags: input.tags || [],
            p_metadata: input.metadata || {},
            p_tenant_hint: tenantId,
            p_dias_expiracao: input.dias_expiracao ?? 60,
            p_default_card_token_id: input.default_card_token_id ?? null,
            ...(hasVehicles ? { p_vehicle_ids: input.vehicle_ids } : {}),
          },
        );

        if (rpcError) {
          logger.error('Erro na RPC create_financeiro_assinatura', { error: rpcError.message });
          throw new Error(rpcError.message);
        }

        const assinaturaId: string | undefined = rpcResult?.assinatura_id;
        const firstCobranca = rpcResult?.first_cobranca;
        const tipoPagamento: string = firstCobranca?.tipo_pagamento || 'pix';
        const firstCobrancaId: string | undefined = firstCobranca?.cobranca_id;

        logger.info('Assinatura criada com sucesso', {
          assinaturaId,
          ciclosGerados: rpcResult?.ciclos_gerados,
        });

        // ETAPA 1.5 — Template de descrição (v1.4.0)
        // Se a descrição contém variáveis ({{...}}), gravamos o TEMPLATE na
        // assinatura (herdado por todos os ciclos) e re-renderizamos o snapshot
        // do 1º ciclo ANTES do envio ao provider.
        if (hasDescricaoVariables(descricaoGeral)) {
          if (assinaturaId) {
            await persistDescricaoTemplate({
              template: descricaoGeral,
              assinaturaId,
              tenantHint: tenantId,
            });
          }
          if (firstCobrancaId) {
            await persistDescricaoTemplate({
              template: descricaoGeral,
              cobrancaId: firstCobrancaId,
              tenantHint: tenantId,
            });
          }
        }



        // ETAPA 2 — emitir ciclo 1 no provider (não-transacional)
        // v2.0.0 (2026-06-17): unificado via helper dispatchCobrancaToProvider.
        // Backend (financial-charge v1.8.0) carrega valor_total, data_vencimento
        // e descricao direto do banco — elimina divergência local↔Asaas que
        // ocorria quando o pro-rata era aplicado mas o payload enviava o valor
        // cheio da assinatura.
        if (firstCobrancaId) {
          const ok = await dispatchCobrancaToProvider({
            cobranca_id: firstCobrancaId,
            conta_corrente_id: input.conta_corrente_id,
            crm_customer_id: input.customer_id,
            tipo_pagamento: tipoPagamento,
            observacoes: input.observacoes || null,
            origin: 'subscription_first_cycle',
          });
          if (!ok) {
            toast.warning('Assinatura criada. Envio do 1º ciclo ao provider será retentado.');
          } else {
            logger.info('Ciclo 1 enviado ao provider com sucesso');
          }
        }

        return { id: assinaturaId, ...rpcResult };
      } finally {
        submittingRef.current = false;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-assinaturas-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
      toast.success('Assinatura criada com sucesso!');
    },
    onError: (error: Error) => {
      logger.error('Erro fatal ao criar assinatura', { error: error.message });
      toast.error(`Erro ao criar assinatura: ${error.message}`);
    },
  });

  return {
    createSubscription,
    isCreating: createSubscription.isPending,
  };
}
